import { useRef } from 'react';

import cn from 'classnames';

import { useIntersection } from 'hooks/useIntersection';
import { useInt } from 'hooks/useInt';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';

import styles from './ReliabilityComparison.module.scss';

import type { ReliabilityColumn, ReliabilityComparisonProps } from './ReliabilityComparison.types';

const CheckIcon = () => (
  <svg viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path d="M2 6.2 4.8 9 10 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DashIcon = () => (
  <svg viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path d="M2.5 6h7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const Column = ({ column }: { column: ReliabilityColumn }) => {
  const { t } = useInt();

  return (
    <div className={cn(styles.column, column.highlighted && styles.columnHighlighted)}>
      {column.highlighted && <span className={styles.badge}>{t('Always on')}</span>}

      <div className={styles.columnHead}>
        <h3 className={styles.columnTitle}>{column.title}</h3>
        <p className={styles.columnSub}>{column.sub}</p>
      </div>

      <ul className={styles.points}>
        {column.points.map((point) => (
          <li key={point.title} className={styles.point}>
            <span className={styles.mark}>{column.highlighted ? <CheckIcon /> : <DashIcon />}</span>
            <div>
              <h4 className={styles.pointTitle}>{point.title}</h4>
              <p className={styles.pointText}>{point.description}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const ReliabilityComparison = ({
  eyebrow,
  title,
  description,
  columns,
  className,
}: ReliabilityComparisonProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(containerRef, { threshold: 0.1 });

  return (
    <section className={cn(styles.section, className)}>
      <div className={cn(styles.container, revealed && styles.containerOn)} ref={containerRef}>
        <div className={styles.head}>
          <TitleBlock align="left" extra={eyebrow} title={title} description={description} />
        </div>

        <div className={styles.grid}>
          <Column column={columns[0]} />
          <span className={styles.divider} aria-hidden="true">
            vs
          </span>
          <Column column={columns[1]} />
        </div>
      </div>
    </section>
  );
};
